import React, { useState } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { Check, X, ChevronDown, Loader2 } from "lucide-react";

/**
 * MultiSelect — thin wrapper around Headless UI Combobox (multiple mode).
 *
 * Props:
 *  - options       [{key, text, value}]
 *  - value         string[] of selected values
 *  - onChange       (values: string[]) => void
 *  - placeholder    string
 *  - loading        boolean
 *  - disabled       boolean
 *  - className      string  (extra classes on root)
 */
const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select...",
  loading = false,
  disabled = false,
  className = "",
}) => {
  const [query, setQuery] = useState("");

  const filtered =
    query === ""
      ? options
      : options.filter((o) =>
          o.text.toLowerCase().includes(query.toLowerCase())
        );

  // Headless UI Combobox in multiple mode passes the full array of selected
  // *option objects* (by reference equality with `value`).  Because we store
  // values as plain strings we pass `value` as the array of selected strings
  // and `onChange` receives the updated array of strings.
  const handleRemove = (val) => {
    onChange(value.filter((v) => v !== val));
  };

  return (
    <Combobox
      multiple
      value={value}
      onChange={(vals) => { setQuery(""); onChange(vals); }}
      onClose={() => setQuery("")}
      disabled={disabled}
    >
      <div className={`relative ${className}`}>
        {/* Trigger area: tags + search input */}
        <div
          className={`flex flex-wrap items-center gap-1.5 min-h-[38px] px-2.5 py-1.5 rounded-lg border text-sm cursor-text
            ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          style={{
            background: "var(--input-bg)",
            borderColor: "var(--border-color)",
            color: "var(--text-primary)",
          }}
        >
          {/* Selected tags */}
          {value.map((v) => {
            const opt = options.find((o) => o.value === v);
            return (
              <span
                key={v}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                style={{
                  background: "var(--bg-tertiary)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border-color)",
                }}
              >
                {opt?.text || v}
                {!disabled && (
                  <button
                    type="button"
                    className="hover:opacity-70 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(v);
                    }}
                    aria-label={`Remove ${opt?.text || v}`}
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            );
          })}

          {/* Search input */}
          <ComboboxInput
            className="border-0 outline-none bg-transparent text-sm flex-1 min-w-[80px] p-0"
            style={{ color: "var(--text-primary)" }}
            placeholder={value.length === 0 ? placeholder : ""}
            onChange={(e) => setQuery(e.target.value)}
            disabled={disabled}
          />

          {/* Right side: loading spinner or chevron */}
          <span className="ml-auto shrink-0 flex items-center pl-1">
            {loading ? (
              <Loader2
                size={14}
                className="animate-spin"
                style={{ color: "var(--text-secondary)" }}
              />
            ) : (
              <ChevronDown
                size={14}
                style={{ color: "var(--text-secondary)" }}
              />
            )}
          </span>
        </div>

        {/* Options dropdown (portal-rendered by Headless UI) */}
        <ComboboxOptions
          anchor="bottom start"
          className="w-[var(--input-width)] rounded-lg border shadow-lg p-1 [--anchor-gap:4px] max-h-60 overflow-y-auto empty:hidden"
          style={{
            background: "var(--card-bg)",
            borderColor: "var(--border-color)",
          }}
        >
          {filtered.length === 0 && query !== "" ? (
            <div
              className="px-3 py-2 text-sm"
              style={{ color: "var(--text-secondary)" }}
            >
              No results found
            </div>
          ) : (
            filtered.map((opt) => (
              <ComboboxOption
                key={opt.key}
                value={opt.value}
                className="group flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors data-[focus]:bg-[var(--bg-secondary)]"
                style={{ color: "var(--text-primary)" }}
              >
                <Check
                  size={14}
                  className="shrink-0 invisible group-data-[selected]:visible"
                  style={{ color: "var(--brand-primary)" }}
                />
                {opt.text}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};

export default MultiSelect;
